import { useEffect, useState } from 'react';

import { useCourseV2 } from '@/features/course/admin/hooks/useCourseV2';
import { useSaveCourse } from '@/administration/pages/Course/store/courseSave/useSaveMyCourse';
import { useXAPISession, XAPISession } from '@/store/xapi/useXAPISession';
import { useXAPIClient } from '@/store/xapi/useXAPIClient';
import { useModularLearningViewerV2 } from '@/pages/ModularLearning/useModularLearningViewerV2';
import { Activity, ActivityType, TreeActivity } from '@/features/course/admin/types/activity';
import { Course, CourseV2 } from '@/administration/pages/Course/store/course/types';
import { ActivityId } from '@/administration/pages/Course/store/types';
import { getActiveDefaultActivityIndex } from '@/features/course/admin/utils/getActiveDefaultActivityIndex';
import { convertActivitiesToSortedNavigationList } from '@/pages/ModularLearning/utils';
import { deepMerge } from '@/utils/datastructure/deep-merge';
import { usePluginTrackActivityProgress } from './plugins/useTrackActivityProgress';
import { usePluginTrackCourseProgress } from './plugins/useTrackCourseProgress';
import { usePluginTrackLastActiveActivity } from './plugins/useTrackLastActiveActivity';
import { usePluginAddResultsActivity } from './plugins/useAddResultsActivity';
import { useLearnerCourseNavigate } from '@/features/course/learner/hooks/useLearnerCourseNavigate';
import { Progress } from '@/administration/pages/Course/store/progress/types';

const AUTO_NAVIGATION_TIMEOUT_DELAY = 500;

type UseCourseViewerV2Props = {
  id: number;
};

export type UseCourseViewerV2Result = {
  course?: CourseV2;
  activities: Activity[];
  treeActivities: TreeActivity[];
  progress?: Progress;
  xapiSession?: XAPISession;
  startCourse: () => void;
  continueCourse: () => void;
  revisitCourse: () => void;
  onSetActivityActive: (activityId: ActivityId) => void;
  onSetPrevActivityActive: () => void;
  onSetNextActivityActive: () => void;
  onSetActivityActiveByIndex: (index: number) => void;
  onSaveCourse: () => void;
  refetchProgress: () => void;
  activityActiveCount: number;
  previousActiveActivityIndex: number;
  activitiesTotalCount: number;
  isCourseSaved: boolean;
  loadingLearning: boolean;
  areAllActivitiesCompleted: boolean;
  loadingProgressInitialized: boolean;
  loadingProgress: boolean;
  loadingSavedLearning: boolean;
  loadingStartCourse: boolean;
  isDrawerOpen: boolean;
  setDrawerOpen: (value: boolean) => void;
  onActivityChange: (id: ActivityId, diff: Partial<Activity>) => void;
};

export const useCourseViewerV2 = ({ id }: UseCourseViewerV2Props): UseCourseViewerV2Result => {
  const navigate = useLearnerCourseNavigate();
  const { data, loading: loadingLearning, refetch: refetchCourse } = useCourseV2(id);
  const {
    handleSavedLearnings: handleSaveCourse,
    isSaved: isCourseSaved,
    loading: loadingSavedLearning,
  } = useSaveCourse(data?.course?.id);
  const { session, loadingSession, register, refetchSession } = useXAPISession(Number(data?.course?.id));
  const xapiClient = useXAPIClient(session);

  const pluginTrackActivityProgress = usePluginTrackActivityProgress({ xapiSession: session, xapiClient });
  const pluginTrackCourseProgress = usePluginTrackCourseProgress({
    xapiSession: session,
    xapiClient,
    course: { id: data?.course?.id, title: data?.course?.title },
  });
  const pluginTrackLastActiveActivity = usePluginTrackLastActiveActivity({ xapiSession: session, xapiClient });
  const pluginAddResultsActivity = usePluginAddResultsActivity({
    xapiSession: session,
    xapiClient,
    course: data?.course as unknown as Course,
  });

  const [loadingStartCourse, setLoadingStartCourse] = useState(false);
  const [loadingProgressInitialized, setLoadingProgressInitialized] = useState(false);

  const arePluginsInitialized =
    pluginTrackActivityProgress.isInitialized &&
    pluginTrackCourseProgress.isInitialized &&
    pluginAddResultsActivity.isInitialized &&
    pluginTrackLastActiveActivity.isInitialized;

  const {
    activities,
    setDrawerOpen,
    isDrawerOpen,
    setActivities,
    treeActivities,
    activityActiveCount,
    onSetActivityActive,
    activitiesTotalCount,
    onSetPrevActivityActive,
    onSetNextActivityActive,
    areAllActivitiesCompleted,
    previousActiveActivityIndex,
    onSetActivityActiveByIndex,
  } = useModularLearningViewerV2();

  useEffect(() => {
    if (loadingProgressInitialized || loadingLearning || loadingSession) return;

    setLoadingProgressInitialized(true);
  }, [loadingLearning, loadingSession, loadingProgressInitialized]);

  useEffect(() => {
    if (!data?.activities || !arePluginsInitialized) return;

    let nextActivities = pluginTrackActivityProgress.onLoadTransformActivities(data?.activities || []);
    nextActivities = pluginAddResultsActivity.onLoadTransformActivities(nextActivities);
    nextActivities = pluginTrackLastActiveActivity.onLoadTransformActivities(nextActivities);

    const activeActivityIndex = nextActivities?.findIndex((activity) => activity?.active);
    const activityToActivateIndex = getActiveDefaultActivityIndex(nextActivities || []);

    if (activityToActivateIndex !== -1 && activeActivityIndex === -1) {
      nextActivities[activityToActivateIndex] = { ...nextActivities[activityToActivateIndex], active: true };
    }

    setActivities(nextActivities || []);
  }, [loadingLearning, arePluginsInitialized]);

  useEffect(() => {
    if (!arePluginsInitialized) return;

    pluginTrackActivityProgress.onActivitesUpdate(activities);
    pluginTrackCourseProgress.onActivitesUpdate(activities);
    pluginTrackLastActiveActivity.onActivitesUpdate(activities);

    const resultsActivity = pluginAddResultsActivity.onActivitesUpdateCreateActivity(activities);
    if (resultsActivity) {
      setActivities((previous) => [...previous, resultsActivity]);

      setTimeout(() => onSetActivityActive({ activityId: resultsActivity.id }), AUTO_NAVIGATION_TIMEOUT_DELAY);
    }
  }, [activities]);

  const onActivityChange = (id: ActivityId, diff: Partial<Activity>): void => {
    setActivities((oldActivities: Activity[]) =>
      oldActivities.map((activityItem: Activity) => (activityItem.id !== id ? activityItem : deepMerge(activityItem, diff))),
    );
  };

  const handleStartCourse = async () => {
    try {
      if (session) return;

      setLoadingStartCourse(true);

      await refetchSession();

      if (session) return;

      await register();

      await refetchSession();

      navigate.navigateToCourseView({ id: Number(data?.course?.id) || 0, title: data?.course?.title || '' });
    } catch {
    } finally {
      setLoadingStartCourse(false);
    }
  };

  const progress = {
    ...(data?.course?.userLastParticipation ? data?.course?.userLastParticipation : {}),
    completedPercentage: (data?.course?.userLastParticipation?.progress || 0) * 100,
    isStarted: !!session?.registration,
  } as Progress;

  const handleContinueCourse = () => {
    navigate.navigateToCourseView({ id: Number(data?.course?.id) || 0, title: data?.course?.title || '' });
  };

  const handleRevisitCourse = () => {
    navigate.navigateToCourseView({ id: Number(data?.course?.id) || 0, title: data?.course?.title || '' });
  };

  // Text (Page) activities only report completion on unmount. When the user clicks "Next" on the
  // last Page in the course, we never navigate away, so it never unmounts and never completes —
  // and the results activity is only added once all activities are completed. Mark the last Page
  // as completed here so the results activity can be created and we can navigate to it.
  const completeLastPageActivityIfNeeded = () => {
    const currentActivity = activities.find((activity) => activity.active);

    if (currentActivity?.type !== ActivityType.Page) return;

    const sortedList = convertActivitiesToSortedNavigationList(activities);
    const currentIndex = sortedList.findIndex((a) => a.id === currentActivity.id);
    const isLastContentActivity = currentIndex === sortedList.length - 1;

    if (isLastContentActivity) {
      onActivityChange(currentActivity.id, { completed: true });
    }
  };

  const handleSetNextActivityActive = () => {
    completeLastPageActivityIfNeeded();
    onSetNextActivityActive();
  };

  return {
    course: data?.course,
    activities,
    treeActivities,
    progress,
    xapiSession: session,

    startCourse: handleStartCourse,
    continueCourse: handleContinueCourse,
    revisitCourse: handleRevisitCourse,
    onSetActivityActive: (activityId) => onSetActivityActive({ activityId }),
    onSetPrevActivityActive,
    onSetNextActivityActive: handleSetNextActivityActive,
    onSetActivityActiveByIndex,
    onSaveCourse: handleSaveCourse,

    refetchProgress: refetchCourse,

    activityActiveCount,
    areAllActivitiesCompleted,
    previousActiveActivityIndex,
    activitiesTotalCount,

    isCourseSaved,

    loadingLearning: loadingLearning,
    loadingProgressInitialized: loadingProgressInitialized,
    loadingProgress: loadingSession || loadingLearning,
    loadingSavedLearning,
    loadingStartCourse,
    isDrawerOpen,
    setDrawerOpen,
    onActivityChange,
  };
};
